
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg'
import { ReactComponent as Error } from '@/assets/svgs/icon-error-message-exclamation.svg'
import { ReactComponent as IconTick } from '@/assets/svgs/icon-account-setting-tick.svg'
import { ReactComponent as IconInformation } from '@/assets/svgs/information-circle.svg'
import { ReactComponent as IconDefaultCompanyLogo } from '@/assets/svgs/company-logo-icon.svg'
import { ReactComponent as IconUploadInfo } from "@/assets/svgs/icon-ds-info.svg";
import { ReactComponent as IconDelete } from "@/assets/svgs/icon-delete.svg";
import iconBuilding from '@/assets/svgs/icon-building.svg'
import CardConfigure from '@/components/CardConfigure'
import Modal from '@/components/Modal'
import useModal from '@/hooks/use-modal'
import rem from '@/utils/rem'
import React, { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { validateEmail } from '@/utils/validate-email'
import classname from 'classnames'
import Button from '@/components/Button'
import API from '@/services/api/login.js'
import API_ACCOUNT from '@/services/api/accountSetting.js'
import Toast from '@/components/Toast'
import useToast from '@/hooks/use-toast'
import Input from '@/components/Input/index'
import validatePassword from '@/utils/validate-password'
import { feature, features as featureConstant } from '@/utils/feature-verifier'
import { useDropzone } from 'react-dropzone'
import Tooltip from '@/components/Tooltip/Tooltip'
import API_PROFILE from '@/services/api/profile.js';
import { useTradex } from '@/hooks/use-tradex/index'
import ModelTermsTable from './modal-terms-table'
import { useFeature } from '@/hooks/use-feature/index'
import PropTypes from 'prop-types';


const Page = ({setCreateModelTerm, modelTermEnabled, termTemplate}) => {

	/*=================================================
		General
	/================================================*/
	const [profile, setProfile] = useState({});
	const isTradex = useTradex()

	useEffect(() => {
		async function fetchProfileDetails() {
			await getProfileDetails();
		}
		fetchProfileDetails();
	}, []);

	async function getProfileDetails() {
		const result = await API_PROFILE.index();
		if (!result) {
			return addToastMessage("Requests API has error", "error");
		}
		const [error, response] = result;
		if (error) {
			return addToastMessage("Requests API has error", "error");
		}
		return setProfile(response);
	}

	const [loading, setLoading] = useState(false);
	const [fileLists, setFileLists] = useState([]);

	/*=================================================
		Modal
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { visible: visibleVerifyUser, show: showVerifyUser, hide: hideVerifyUser } = useModal()
	const { visible: visibleUpdateEmail, show: showUpdateEmail, hide: hideUpdateEmail } = useModal()
	const { visible: visibleUpdateEmailSuccess, show: showUpdateEmailSuccess, hide: hideUpdateEmailSuccess } = useModal()
	const { visible: visibleUpdatePassword, show: showUpdatePassword, hide: hideUpdatePassword } = useModal()
	const { visible: visibleUpdatePasswordSuccess, show: showUpdatePasswordSuccess, hide: hideUpdatePasswordSuccess } = useModal()
	const { visible: visibleUploadCompanyLogo, show: showUploadCompanyLogo, hide: hideUploadCompanyLogo } = useModal()
	
	const isIGPNILegalRequirementsEnabled = useFeature(featureConstant.IG_PNI_LEGAL_REQUIREMENTS)

	/*=================================================
		Dropzone for Letterhead Image
	/================================================*/
	const onDrop = useCallback((acceptedFiles) => {
		if ((acceptedFiles[0]?.size) > 500000) {
			return addToastMessage("File uploaded size is more than 500kb", "error")
		}
		const newFiles = acceptedFiles.map(file => Object.assign(file, {
			preview: URL.createObjectURL(file)
		}))
		setFileLists((prevFiles) => [...prevFiles, ...newFiles]);
	}, []);

	const onDropRejected = useCallback(_rejectedFiles => {
		return addToastMessage("Invalid file format. Please upload a PNG or SVG file.", "error");
	}, []);

	const { getRootProps, getInputProps } = useDropzone({ onDrop, onDropRejected, multiple: false, accept: "image/png, .svg" })

	const { register, handleSubmit, formState: { errors, isValid }, getValues, setValue } = useForm({
		mode: 'onChange'
	})

	const toolTipContent = `Upload your company's official letterhead image. <br /> This image will be displayed at the top of all generated ETR documents. <br /> Recommended image aspect ratio: 3:1 width:height.`;

	function onAddAttachment(e) {
		e.stopPropagation();
		document.getElementById(`input-file`).click();
	}

	async function requestAction() {
		setLoading(true);
		const [getUrlError, urlResult] = await API_ACCOUNT.getSignedUrl(profile.organization.orgId, fileLists, 'document-logo')
		if (getUrlError) {
			setLoading(false);
			return addToastMessage(getUrlError?.message, "error")
		}
		const [uploadError] = await API_ACCOUNT.uploadFileToSignedURL(urlResult.uploadURL, fileLists)
		if (uploadError) {
			setLoading(false);
			return addToastMessage(uploadError?.message, "error")
		}
		const body = {
			key: urlResult.key,
			fileName: fileLists[0]?.name
		}
		const [documentError, documentResponse] = await API_ACCOUNT.uploadDocumentLogo(profile.organization.orgId, body)
		if (documentError) {
			setLoading(false);
			return addToastMessage(documentError?.message, "error")
		}
		setLoading(false);
		getProfileDetails();
		hideUploadCompanyLogo();
		return addToastMessage(documentResponse?.message, "success")
	}

	const [error, setError] = useState(null)
	const [verifyUser, setVerifyUser] = useState(null)
	const [newEmail, setNewEmail] = useState(null)

	function resetValue() {
		setValue('currentPassword', "");
		setValue('newPassword', "");
		setValue('verifyPassword', "");
	}

	const inputClass = classname({
		'input--error': error
	})

	async function verifyUserHandler({ password }) {
		if (password === "") {
			return setError("Required")
		}

		const [error, response] = await API.create({ email: profile.email, password: password })

		if (error) {
			return setError('Incorrect password')
		}

		hideVerifyUser()
		showUpdateEmailModal()
	}

	function showUpdateEmailModal() {
		setError(null)
		setNewEmail(null)
		showUpdateEmail()
	}

	function updateEmailHandler({ email }) {
		if (email === "") {
			return setError("Required")
		}
		if (!validateEmail(email)) {
			return setError("Please provide a valid email")
		}
		hideUpdateEmail()
		showUpdateEmailSuccess()
	}

	function showUpdatePasswordModal() {
		setError(null)
		showUpdatePassword()
	}

	async function updatePasswordHandler(data) {
		if (loading) {
			return
		}

		setLoading(true)

		const currentPassword = data.currentPassword
		const newPassword = data.newPassword
		setError(null)

		const [errorResetPassword, responseResetPassword] = await API_ACCOUNT.update(profile.id, { password: newPassword, currentPassword: currentPassword })

		setLoading(false)
		if (errorResetPassword) {
			resetValue()
			return setError('Failed to update password')
		}

		addToastMessage("Your password has been updated.", "success")

		hideUpdatePassword()
		showUpdatePasswordSuccess()
	}

	const profileImage = profile?.organization?.iconUrl ? profile?.organization?.iconUrl : iconBuilding

	return (
		<>
			<div className="account-setting-box__layout">
				<div className="account-setting-box__top">
					<div className="account-setting-box__top__background">
						<div className="account-setting-avatar__top__image" style={{ backgroundImage: `url(${profileImage})`, textAlign: 'center' }}></div>
					</div>
					<div className="account-setting-box__top__text">
						<p className="account-setting-box__top__text__name">{profile?.firstName}</p>
						<p className="account-setting-box__top__text__org">{profile?.organization?.name}</p>
					</div>
				</div>
				<div className="account-setting-box__bottom">
					<div className="account-setting-box__bottom__email">
						<span className="account-setting-box__bottom__text">Email Address</span>
						<input type="text" placeholder={profile?.email} style={{ width: rem(368) }} disabled={true} />
					</div>
					<div className="account-setting-box__bottom__password">
						<span className="account-setting-box__bottom__text">Password</span>
						<input type="password" style={{ width: rem(368) }} placeholder="*************" disabled={true} />
						<Button ghost text="Change" submit={() => showUpdatePasswordModal()} />
					</div>
					{feature(featureConstant.ETR_LOGO_RENDERER) && isTradex && <div className="account-setting-box__bottom__company-logo">
						<span className="account-setting-box__bottom__company-text">
							Letterhead Image <div data-tooltip-id="tooltip" data-tooltip-html={toolTipContent} data-tooltip-place='bottom-end'><IconUploadInfo /></div>
						</span>
						{profile?.organization?.documentLogo ? <div>
							<div className='account-setting-box__bottom__company-logo__renderer'>
								<div className='account-setting-box__bottom__company-icon'>
									<img src={`https://blob.sgtradex.io/${profile?.organization.documentLogo.key}`} alt="Letterhead" />
								</div>
								<div className='account-setting-box__bottom__company-name'>{profile?.organization.documentLogo.fileName}</div>
							</div>
							<div className='account-setting-box__bottom__format-text'>Supported Format: PNG, or SVG. Maximum 500kB per file</div>
						</div> : <div>
							<div className='account-setting-box__bottom__company-logo__icon-text'>
								<IconDefaultCompanyLogo />
								<span>No image uploaded yet</span>
							</div>
							<div className='account-setting-box__bottom__format-text'>Supported Format: PNG, or SVG. Maximum 500kB per file</div>
						</div>}
						<Button
							ghost
							data-testid="addImage"
							text={profile?.organization?.documentLogo ? "Change" : "Add Image"}
							submit={() => {
								showUploadCompanyLogo();
								setFileLists([]);
							}}
						/>
					</div>}
				</div>
				{isIGPNILegalRequirementsEnabled && modelTermEnabled && (
					<ModelTermsTable setCreateModelTerm={setCreateModelTerm} termTemplate={termTemplate}/>
				)}
			</div>

			<Modal visible={visibleVerifyUser} variant='medium' close={hideVerifyUser}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Update Email Address
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<form id="verifyUser" onSubmit={handleSubmit(verifyUserHandler)}>
								<div className="account-setting-box__modal__col__email">
									<div className="account-setting-box__modal__desc" >To continue, please verify your identity</div>
									<div style={{ marginTop: rem(6) }}>
										<input name="password" className={inputClass} type="password" placeholder="Enter current password" ref={register({ required: true })} onChange={(e) => setVerifyUser(e.target.value)} />
										{error && (
											<div className="t--required account-setting-box__modal__error">
												<div><Error /></div>
												<div>{error}</div>
											</div>
										)}
									</div>
								</div>
							</form>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel" submit={() => hideVerifyUser()} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button form="verifyUser" text="Confirm" disabled={!verifyUser} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={visibleUpdateEmail} variant='medium' close={hideUpdateEmail}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Update Email Address
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<form id="updateEmail" onSubmit={handleSubmit(updateEmailHandler)}>
								<div className="account-setting-box__modal__col__email">
									<div className="account-setting-box__modal__desc" >Enter a New Email Address</div>
									<div style={{ marginTop: rem(6) }}>
										<input name="email" className={inputClass} type="text" placeholder="Type here" ref={register({ required: true })} onChange={(e) => setNewEmail(e.target.value)} autoComplete="off" />
										{error && (
											<div className="t--required account-setting-box__modal__error">
												<div><Error /></div>
												<div>{error}</div>
											</div>
										)}
									</div>
								</div>
							</form>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel" submit={() => hideUpdateEmail()} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button form="updateEmail" disabled={!newEmail} text="Confirm" />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={visibleUpdateEmailSuccess} variant='medium' close={hideUpdateEmailSuccess}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Update Email Address
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="account-setting-box__modal__success__email">
								<div style={{ marginBottom: rem(14) }}><IconTick /></div>
								<div className="account-setting-box__modal__success__text">
									<p>Your email is updated.</p>
									<p>We sent a mail to {newEmail} for you to verify.</p>
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Close" submit={() => hideUpdateEmailSuccess()} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={visibleUpdatePassword} variant='medium' close={hideUpdatePassword}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Change Password
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<form id="updatePassword" onSubmit={handleSubmit(updatePasswordHandler)}>
								<div className="account-setting-box__modal__cols">
									<div className="account-setting-box__modal__col">
										<Input
											name="currentPassword"
											label="Current Password"
											register={register({ required: "Required" })}
											placeholder="Type here"
											type="Password"
										/>
									</div>
									<div className="account-setting-box__modal__col">
										<Input
											name="newPassword"
											label="New Password"
											register={register({
												required: "Required",
												validate: {
													checkLength: v => v.length > 8 || "Error",
													verifyPasswordRequirement: v => validatePassword(v) || "Error",
													verifyPassword2: v => v !== getValues().currentPassword || "The new password you entered is the same as the old password. Please enter a different password",
													checkPassword: v => v !== profile?.email || "Your password cannot be the same as your username. Please enter a different password."
												}
											})}
											placeholder="Type here"
											type="password"
										/>
									</div>
									<div className="account-setting-box__modal__col">
										<Input
											name="verifyPassword"
											label="Confirm New Password"
											register={register({
												required: "Required",
												validate: {
													verifyPassword3: v => v === getValues().newPassword || "Passwords do not match"
												}
											})}
											placeholder="Type here"
											error={errors.verifyPassword}
											errorMessage={errors.verifyPassword?.message}
											type="password"
										/>
									</div>

									{errors.newPassword && errors.newPassword?.message !== "Error" && (
										<div className="account-setting-box__modal__col resetPassword-box__caption">
											{errors.newPassword?.message}
										</div>
									)}
									{error && (
										<div className="account-setting-box__modal__col resetPassword-box__caption">
											{error}
										</div>
									)}

									<div className="account-setting-box__modal__col resetPassword-box__caption">
										New password must contain minimum 14 characters and at least 1 uppercase letter,
										lowercase letter, symbol, and number.
									</div>
								</div>
							</form>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button form="updatePassword" disabled={!isValid} loading={loading} text="Confirm" />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={visibleUpdatePasswordSuccess} variant='medium' close={hideUpdatePasswordSuccess}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Change Password
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
						<CardConfigure.Header.Close onClick={() => hideUpdatePasswordSuccess()}>
							<IconClose />
						</CardConfigure.Header.Close>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="account-setting-box__modal__success__password">
								<div style={{ marginBottom: rem(24) }}><IconTick /></div>
								<p>Your password has been changed.</p>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Close" submit={() => hideUpdatePasswordSuccess()} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={visibleUploadCompanyLogo} variant='medium' close={hideUploadCompanyLogo}>
				<CardConfigure style={{ maxWidth: rem(666), position: 'relative', top: rem(40) }}>
					<CardConfigure.Header>
						<CardConfigure.Header.Icon><IconInformation /></CardConfigure.Header.Icon>
						<CardConfigure.Header.Title>Add Image</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="account-settings__modal-logo__heading">
								<div className="account-settings__modal-logo__heading-details-left">
									<div className="account-settings-modal-logo__company-text">Letterhead Image
										<div data-tooltip-id="tooltip" data-tooltip-html={toolTipContent}><IconUploadInfo /></div>
									</div>
								</div>
								<div className="account-settings__modal-logo__heading-details-right">
									<div onClick={() => setFileLists([])} data-testid="clear-all-attachments" role='none'>
										Clear Attachments
									</div>
								</div>
							</div>
							{fileLists?.length === 0 && <div
								className="account-settings__modal-logo__heading-details-drop"
								onClick={(e) => onAddAttachment(e)}
								role='none'
								data-testid="attachments-drop"
							>
								+ Drop your attachment here or <span className="account-settings__modal-logo__heading-details__drop-browse">browse</span>.
							</div>}
							<div {...getRootProps()}>
								<input
									type="file"
									className="hidden"
									id={`input-file`}
									data-testid="input-fileupload"
									{...getInputProps()}
								></input>
							</div>
							{fileLists?.length === 0 && <div className='account-setting-box__bottom__format-text'>Supported Format: PNG, or SVG. Maximum 500kB per file</div>}
							{fileLists?.length > 0 && <div className="account-settings__logo-renderer">
								<div className='account-settings__logo-renderer__image'>
									<img src={fileLists[0]?.preview} alt="Letterhead" />
								</div>
								<div className='account-settings__logo-renderer__image-name'>{fileLists[0]?.name}</div>
								<div className='account-settings__logo-renderer__percentage'>100%</div>
								<div className='account-settings__logo-renderer__delete' onClick={() => setFileLists([])} role='none' data-testid="remove-file-icon"><IconDelete /></div>
							</div>}
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button text="Cancel" ghost noborder data-testid="company-logo-cancel" submit={() => hideUploadCompanyLogo()} />
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<Button disabled={fileLists?.length === 0} round loading={loading} data-testid="company-logo-confirm" text="Confirm" submit={() => requestAction()} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
				<Tooltip />
			</Modal>

			<Toast toastList={toastList} />
		</>
	)
}

export default Page

Page.propTypes = {
	setCreateModelTerm: PropTypes.func,
	modelTermEnabled: PropTypes.bool,
	termTemplate: PropTypes.object,
}
